// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useState, type ReactNode } from 'react';

import { useCaseStore } from '../../../app/bygning/CaseDataContext';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { modelLayerOn, splitMeshes } from '../../../bygning/modelLayers';
import { EXPERT_ENTRIES } from '../../../app/navigation';
import { toElementFilterState } from '../../../viewport/elementFilter';
import { Checkbox } from '../../Checkbox';
import { SegmentedControl } from '../../SegmentedControl';
import { TextLink } from '../../TextLink';
import { Panel } from '../Panel';
import { GeometrySection } from './GeometrySection';
import { PanoramaList } from './PanoramaList';
import { PipelineSection } from './PipelineSection';
import { PointCloudSection } from './PointCloudSection';
import { RowExpander } from './RowExpander';
import { MeasureSection, ScanSection } from './ScanSection';
import { useSettings } from './SettingsContext';
import { RENDERABLE_TYPES, VIEW_LABELS, cloudOn, pickPrimary, type SettingsAction } from './settingsState';
import type { CameraProjection } from '../../../viewport/cameraViews';
import styles from './ModelSettings.module.css';

export { SettingsProvider, useSettings } from './SettingsContext';

/** The pipeline pages have their own links under Ekspert › Pipeline. */
const EXPERT_LINKS = EXPERT_ENTRIES.filter((e) => !e.to.startsWith('/pipeline'));

const PROJECTION: readonly { value: CameraProjection; label: string }[] = [
  { value: 'perspective', label: 'Perspektiv' },
  { value: 'orthographic', label: 'Ortografisk' },
];
/** Låst plan is orthographic: the choice waits for the lock to come off. */
const PROJECTION_LOCKED = PROJECTION.map((item) => ({ ...item, disabled: true }));

const count = (n: number) => n.toLocaleString('da-DK');

/** One section of the panel; `h4` under the Panel's `h3` title. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className={styles.section} aria-labelledby={id}>
      <h4 id={id} className={styles.heading}>
        {title}
      </h4>
      {children}
    </section>
  );
}

/**
 * Modelindstillinger — the gear panel in the left slot (v8 prototype), a
 * `Panel` in a `stacked` tile: its header stays while the sections scroll.
 * What the Ground draws (Lag — Punktsky and Geometri unfold their own
 * settings: how the points are drawn; the lights and Trådnet for the
 * building elements, which are the 3D model), the camera (Visning), where
 * the model came from (Scanning) and the expert pages with the pipeline's
 * latest runs and the reconstruction mesh as a debug layer (Ekspert). Reads and edits the settings through
 * `SettingsContext`; the Ground renders them. The clipping box is a tool on
 * the Ground itself; the theme is in the ⌘K palette and the user menu in the
 * top bar.
 */
export function ModelSettings({ onClose, onImport }: { onClose?: () => void; onImport?: () => void }) {
  const { state, dispatch, facts } = useSettings();
  // Importér BIM-model writes to the case: editors and owners only.
  const canEdit = useCanEdit();
  const { data } = useCaseStore();
  const set = (patch: Extract<SettingsAction, { type: 'set' }>['patch']) => dispatch({ type: 'set', patch });

  const meshes = facts.meshes;
  // The scan's own mesh is Mesh; an imported model's categories are Model.
  const split = splitMeshes(meshes);
  const mesh = split.scan;

  const meshHint =
    meshes === null ? 'indlæses…' : !mesh ? 'ikke i projektet' : facts.meshError ? 'kunne ikke indlæses' : facts.meshLoading ? 'indlæses…' : mesh.name;

  // Lag › Geometri (frontend-t02, r22.27): the persisted hidden-set/storey filter, hydrated for elementFilter.ts's pure functions.
  const elementFilter = toElementFilterState(state.buildingElements);

  const [browsePanos, setBrowsePanos] = useState(false);

  // Lag › Punktsky reads the cloud list the Ground published.
  const clouds = facts.clouds;
  const renderable = (clouds ?? []).filter((c) => RENDERABLE_TYPES.has(c.type));
  const primary = pickPrimary(clouds);

  return (
    <Panel
      title="Modelindstillinger"
      fill
      action={
        onClose && (
          <TextLink size="sm" onClick={onClose}>
            Luk
          </TextLink>
        )
      }
    >
      <div className={styles.panel}>
        <Section title="Lag">
          <div className={styles.checks}>
            {clouds === null && <Checkbox label="Punktsky" checked readOnly disabled hint="indlæses…" />}
            {clouds !== null && renderable.length === 0 && (
              <Checkbox label="Punktsky" checked={false} readOnly disabled hint="ikke i projektet" />
            )}
            {renderable.map((c) => {
              const row = (
                <Checkbox
                  key={c.name}
                  label={renderable.length > 1 || c.name !== 'cloud' ? `Punktsky · ${c.name}` : 'Punktsky'}
                  checked={cloudOn(state, c.name, primary?.name ?? null)}
                  onChange={(e) => dispatch({ type: 'cloud', name: c.name, on: e.target.checked })}
                  hint={`${count(c.point_count)} pkt.`}
                />
              );
              // The point settings apply to the whole layer: they unfold from the primary cloud's row.
              return c.name === primary?.name ? (
                <RowExpander key={c.name} row={row} label="Punktsky-indstillinger">
                  <PointCloudSection />
                </RowExpander>
              ) : (
                row
              );
            })}
            <Checkbox
              label="Panoramaer"
              checked={state.panoramas && facts.panoramas !== 0}
              onChange={(e) => set({ panoramas: e.target.checked })}
              disabled={facts.panoramas === 0}
              hint={facts.panoramas === null ? null : facts.panoramas === 0 ? 'ingen i projektet' : `${facts.panoramas} optagepositioner`}
            />
            {facts.panoramas !== 0 && (
              <TextLink
                size="sm"
                className={styles.subLink}
                aria-expanded={browsePanos}
                onClick={() => setBrowsePanos((o) => !o)}
              >
                {browsePanos ? 'Skjul panoramaerne' : `Gennemse panoramaer${facts.panoramas ? ` (${facts.panoramas})` : ''}`}
              </TextLink>
            )}
            {browsePanos && <PanoramaList />}
            <Checkbox label="Rum-etiketter" checked={state.roomLabels} onChange={(e) => set({ roomLabels: e.target.checked })} />
            <GeometrySection
              tallies={facts.buildingElementTallies}
              state={elementFilter}
              wireframe={state.wireframe}
              lighting={state.lighting}
              dispatch={dispatch}
            />
          </div>
          {split.model.map((group) => (
            <div key={group.source} className={styles.modelGroup} role="group" aria-label={`Model fra ${group.sourceLabel}`}>
              <h5 className={styles.subHeading}>Model · {group.sourceLabel}</h5>
              <div className={styles.checks}>
                {group.layers.map((layer) => (
                  <Checkbox
                    key={layer.name}
                    label={layer.label}
                    checked={modelLayerOn(state.modelLayers, layer, state.planLock)}
                    onChange={(e) => dispatch({ type: 'modelLayer', name: layer.name, on: e.target.checked })}
                  />
                ))}
              </div>
            </div>
          ))}
          {onImport && canEdit && (
            <div className={styles.row}>
              <TextLink size="sm" onClick={onImport}>
                {split.model.length > 0 ? 'Importér BIM-model igen…' : 'Importér BIM-model (Speckle / IFC)…'}
              </TextLink>
            </div>
          )}
        </Section>

        <Section title="Visning">
          <Checkbox
            label="Låst plan"
            checked={state.planLock}
            onChange={(e) => set({ planLock: e.target.checked })}
            hint="set ovenfra"
          />
          <SegmentedControl
            aria-label="Projektion"
            className={styles.seg}
            items={state.planLock ? PROJECTION_LOCKED : PROJECTION}
            value={state.planLock ? 'orthographic' : state.projection}
            onChange={(projection) => set({ projection })}
          />
          <div className={styles.views} role="group" aria-label="Faste visninger">
            {VIEW_LABELS.map(({ preset, label }) => (
              <button
                key={preset}
                type="button"
                className={styles.viewButton}
                onClick={() => dispatch({ type: 'view', preset })}
                disabled={state.planLock && preset !== 'top'}
              >
                {label}
              </button>
            ))}
          </div>
          <div className={styles.row}>
            <TextLink size="sm" onClick={() => dispatch({ type: 'frame' })}>
              Centrér modellen
            </TextLink>
          </div>
        </Section>

        <Section title="Scanning">
          <ScanSection survey={data.summary} instances={facts.instances} />
          <MeasureSection />
        </Section>

        <Section title="Ekspert">
          {mesh && (
            <Checkbox
              label="Rekonstruktionsmesh (fejlsøgning)"
              checked={state.mesh}
              onChange={(e) => set({ mesh: e.target.checked })}
              hint={meshHint}
            />
          )}
          <h5 className={styles.subHeading}>Pipeline</h5>
          <PipelineSection />
          <ul className={styles.links}>
            {EXPERT_LINKS.map((e) => (
              <li key={e.to}>
                <TextLink size="sm" to={e.to}>
                  {e.label}
                </TextLink>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </Panel>
  );
}
